// ============================================================================
// FSA — Shared sectioned-narrative editor (section rail + TipTap editor)
// ============================================================================
//
// One editor for every sectioned narrative (ADR-0024 §3):
//   · POLICY notes (Accounting Policies / Corporate Information) — via
//     PolicyEditorPane, which maps the note workspace keys
//   · SECTIONS narrative components (Board's Report) — via
//     SectionedReportPane, which adds applicability-driven variants
//
// The component is storage-agnostic: callers pass the template sections
// (with each section's effective default content already resolved) and the
// override state, and receive the next state on every change. Callers own
// the workspace keys, so neither consumer can drop the other's fields.
//
// Section management:
//   · Add custom section (button at bottom of rail)
//   · Remove custom section (trash icon — custom sections only)
//   · Rename section (pencil — custom_title on template, title on added)
//   · Hide / show section (eye — template sections only)
//   · Restore default (revert — clears the content override)
//   · Reorder (up / down arrows — persists via `order`)
//
// Editor identity: TipTap reads its content ONLY at creation, so the editor
// is keyed on contentVersion (bumped by the host each time it loads saved
// data), a restore nonce, the section code, and the section's variantKey
// (so switching an applicability variant re-creates the editor with the new
// default text).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import TiptapEditor, { type DataInsertOptions } from './TiptapEditor';

/** One template-defined section, with its effective default already chosen by the caller. */
export interface SectionTemplate {
  code: string;
  title: string;
  /** Default TipTap doc for this section (after variant selection); null = empty. */
  defaultContent: unknown | null;
  /** Changes when the effective default changes (e.g. applicability variant) — re-keys the editor. */
  variantKey?: string;
}

/** Per-section override. Extra keys (e.g. variant_override) are preserved untouched. */
export interface SectionOverride {
  content?: unknown;
  is_hidden?: boolean;
  custom_title?: string;
  [key: string]: unknown;
}

export interface AddedSection {
  local_id: string;
  title: string;
  content?: unknown;
  display_order: number;
}

export interface SectionedState {
  sectionData: Record<string, SectionOverride>;
  addedSections: AddedSection[];
  order?: string[];
}

interface Props {
  templateSections: SectionTemplate[];
  state: SectionedState;
  onChange: (next: SectionedState) => void;
  readOnly?: boolean;
  /** Bumped by the host each time it loads saved data — forces editor re-creation. */
  contentVersion?: number;
  /** Optional header strip rendered above the editor for the selected section. */
  renderSectionHeader?: (code: string, isCustom: boolean) => React.ReactNode;
  /** Optional marker rendered on each rail row (e.g. an applicability dot). */
  renderRailBadge?: (code: string, isCustom: boolean) => React.ReactNode;
  /** Rail heading — "Policy sections", "Report sections". */
  railTitle?: string;
  /** ADR-0024 — enables the toolbar's Insert field / Insert data table pickers. */
  dataInsertOptions?: DataInsertOptions;
  /** Select this section (e.g. from a checklist item). A new nonce re-selects the same code. */
  focusRequest?: { code: string; nonce: number } | null;
  /** Told whenever the selected section changes (the document side panel follows it). */
  onSelectedChange?: (code: string | null) => void;
  /** The rail can collapse to section numbers (remembered per user). */
  collapsibleRail?: boolean;
  /** Rendered to the right of the editor (the document side panel). */
  rightPanel?: React.ReactNode;
  /** Small text beside the section title (statutory references, annexure pill). */
  renderTitleExtra?: (code: string, isCustom: boolean) => React.ReactNode;
  /** Controls at the right end of the section-title row (mode switch, badges). */
  headerRight?: React.ReactNode;
}

interface SectionView {
  code: string;
  title: string;
  isEdited: boolean;
  isHidden: boolean;
  isCustom: boolean;
  defaultContent: unknown | null;
  variantKey: string;
}

/** An override with no meaningful value left is dropped to keep the JSON small. */
function isEmptyOverride(entry: SectionOverride): boolean {
  return Object.values(entry).every(v => v == null || v === '');
}

const SectionedNarrativeEditor: React.FC<Props> = ({
  templateSections,
  state,
  onChange,
  readOnly = false,
  contentVersion = 0,
  renderSectionHeader,
  renderRailBadge,
  railTitle = 'Sections',
  dataInsertOptions,
  focusRequest,
  onSelectedChange,
  collapsibleRail = false,
  rightPanel,
  renderTitleExtra,
  headerRight,
}) => {
  const [restoreNonce, setRestoreNonce] = useState(0);
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    if (!collapsibleRail) return false;
    try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed(c => {
      try { localStorage.setItem(RAIL_KEY, c ? '0' : '1'); } catch { /* storage unavailable */ }
      return !c;
    });
  }, []);
  const { sectionData, addedSections, order } = state;

  const sections = useMemo(() => {
    const out: SectionView[] = [];
    for (const t of templateSections) {
      const ov = sectionData[t.code];
      out.push({
        code: t.code,
        title: ov?.custom_title ?? t.title,
        isEdited: ov?.content != null,
        isHidden: ov?.is_hidden === true,
        isCustom: false,
        defaultContent: t.defaultContent,
        variantKey: t.variantKey ?? '',
      });
    }
    for (const a of addedSections) {
      const ov = sectionData[a.local_id];
      out.push({
        code: a.local_id,
        title: ov?.custom_title ?? a.title,
        isEdited: true,
        isHidden: ov?.is_hidden === true,
        isCustom: true,
        defaultContent: a.content ?? null,
        variantKey: '',
      });
    }
    if (order && order.length > 0) {
      const indexOf = new Map<string, number>();
      order.forEach((c, i) => { if (!indexOf.has(c)) indexOf.set(c, i); });
      out.sort((a, b) => {
        const ai = indexOf.has(a.code) ? indexOf.get(a.code)! : Number.MAX_SAFE_INTEGER;
        const bi = indexOf.has(b.code) ? indexOf.get(b.code)! : Number.MAX_SAFE_INTEGER;
        return ai - bi;
      });
    }
    return out;
  }, [templateSections, sectionData, addedSections, order]);

  const [selectedCode, setSelectedCode] = useState<string | null>(sections[0]?.code ?? null);

  useEffect(() => {
    if (sections.length === 0) {
      setSelectedCode(null);
      return;
    }
    if (selectedCode == null || !sections.some(s => s.code === selectedCode)) {
      setSelectedCode(sections[0].code);
    }
  }, [sections, selectedCode]);

  useEffect(() => {
    if (focusRequest && sections.some(s => s.code === focusRequest.code)) setSelectedCode(focusRequest.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest?.nonce]);

  const selected = sections.find(s => s.code === selectedCode) ?? null;

  useEffect(() => { onSelectedChange?.(selectedCode); }, [selectedCode, onSelectedChange]);

  const effectiveContent = useMemo(() => {
    if (!selected) return undefined;
    const ov = sectionData[selected.code];
    if (ov?.content != null) return ov.content;
    return selected.defaultContent ?? undefined;
  }, [selected, sectionData]);

  // ---- Mutators ----------------------------------------------------------

  const emit = useCallback((patch: Partial<SectionedState>) => {
    onChange({ ...state, ...patch });
  }, [state, onChange]);

  const mutateEntry = useCallback((code: string, patch: Partial<SectionOverride>) => {
    const nextEntry: SectionOverride = { ...(sectionData[code] ?? {}), ...patch };
    const nextDict = { ...sectionData };
    if (isEmptyOverride(nextEntry)) delete nextDict[code];
    else nextDict[code] = nextEntry;
    emit({ sectionData: nextDict });
  }, [sectionData, emit]);

  const handleEditorChange = useCallback((doc: unknown) => {
    if (!selected) return;
    mutateEntry(selected.code, { content: doc });
  }, [selected, mutateEntry]);

  const handleToggleHidden = useCallback((code: string) => {
    const cur = sectionData[code]?.is_hidden === true;
    mutateEntry(code, { is_hidden: cur ? undefined : true });
  }, [sectionData, mutateEntry]);

  const handleRename = useCallback((code: string, currentTitle: string) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt('Section title:', currentTitle);
    if (next == null) return;
    const trimmed = next.trim();
    if (trimmed === '' || trimmed === currentTitle) {
      mutateEntry(code, { custom_title: undefined });
      return;
    }
    const addedIdx = addedSections.findIndex(a => a.local_id === code);
    if (addedIdx >= 0) {
      const nextAdded = addedSections.slice();
      nextAdded[addedIdx] = { ...nextAdded[addedIdx], title: trimmed };
      emit({ addedSections: nextAdded });
      return;
    }
    mutateEntry(code, { custom_title: trimmed });
  }, [addedSections, emit, mutateEntry]);

  const handleRestoreDefault = useCallback((code: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Restore the template default content for this section?')) return;
    mutateEntry(code, { content: undefined });
    // The live editor still holds the edited text until it is re-created.
    setRestoreNonce(n => n + 1);
  }, [mutateEntry]);

  const move = useCallback((code: string, delta: -1 | 1) => {
    const codes = sections.map(s => s.code);
    const idx = codes.indexOf(code);
    const to = idx + delta;
    if (idx < 0 || to < 0 || to >= codes.length) return;
    const next = codes.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    emit({ order: next });
  }, [sections, emit]);

  const handleAdd = useCallback(() => {
    // eslint-disable-next-line no-alert
    const title = window.prompt('New section title:');
    if (title == null) return;
    const trimmed = title.trim();
    if (trimmed === '') return;
    const localId = generateLocalId();
    const newSection: AddedSection = {
      local_id: localId,
      title: trimmed,
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
      display_order: 1000 + addedSections.length * 10,
    };
    const codes = sections.map(s => s.code);
    emit({
      addedSections: [...addedSections, newSection],
      order: codes.length > 0 ? [...codes, localId] : undefined,
    });
    setSelectedCode(localId);
  }, [sections, addedSections, emit]);

  const handleRemove = useCallback((code: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this custom section? Its content will be discarded.')) return;
    const nextData = { ...sectionData };
    delete nextData[code];
    emit({
      addedSections: addedSections.filter(a => a.local_id !== code),
      sectionData: nextData,
      order: order?.filter(c => c !== code),
    });
  }, [sectionData, addedSections, order, emit]);

  // ---- Render ------------------------------------------------------------

  if (sections.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          No sections defined.
        </div>
        {!readOnly && (
          <button type="button" className="btn btn-primary btn-sm" onClick={handleAdd}
            style={{ marginTop: '0.75rem' }}>
            <IconSystem.add size={14} /> Add section
          </button>
        )}
      </div>
    );
  }

  if (railCollapsed) {
    return (
      <div style={paneStyle}>
        <aside style={{ ...sectionListStyle, width: 56 }}>
          <header style={{ ...sectionListHeaderStyle, justifyContent: 'center', padding: '0.5rem 0' }}>
            <button type="button" onClick={toggleRail} style={railToggleStyle} title="Show section titles" aria-label="Show section titles">
              <IconSystem.chevronRight size={14} />
            </button>
          </header>
          <div style={sectionListBodyStyle}>
            {sections.map((s, idx) => (
              <button
                key={s.code}
                type="button"
                onClick={() => setSelectedCode(s.code)}
                title={s.title}
                style={{
                  ...collapsedRowStyle,
                  background: s.code === selectedCode ? 'var(--bg-secondary)' : 'transparent',
                  borderLeftColor: s.code === selectedCode ? '#2563eb' : 'transparent',
                  fontWeight: s.code === selectedCode ? 700 : 500,
                  opacity: s.isHidden ? 0.5 : 1,
                }}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </aside>
        {renderEditor()}
        {rightPanel}
      </div>
    );
  }

  return (
    <div style={paneStyle}>
      <aside style={sectionListStyle}>
        <header style={sectionListHeaderStyle}>
          <span>{railTitle}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={countStyle}>{sections.length}</span>
            {collapsibleRail && (
              <button type="button" onClick={toggleRail} style={railToggleStyle} title="Collapse to section numbers" aria-label="Collapse sections">
                <IconSystem.chevronLeft size={14} />
              </button>
            )}
          </span>
        </header>
        <div style={sectionListBodyStyle}>
          {sections.map((s, idx) => {
            const isSelected = s.code === selectedCode;
            return (
              <div
                key={s.code}
                style={{
                  ...sectionRowStyle,
                  background: isSelected ? 'var(--bg-secondary)' : 'transparent',
                  borderLeftColor: isSelected ? '#2563eb' : 'transparent',
                  opacity: s.isHidden ? 0.55 : 1,
                }}
              >
                <button
                  type="button"
                  onClick={() => setSelectedCode(s.code)}
                  style={sectionRowMainStyle}
                  title={s.title}
                >
                  <span style={sectionIndexStyle}>{idx + 1}.</span>
                  <span style={{
                    ...sectionTitleStyle,
                    fontWeight: isSelected ? 600 : 400,
                    fontStyle: s.isHidden ? 'italic' : 'normal',
                  }}>
                    {s.title}
                  </span>
                  {renderRailBadge?.(s.code, s.isCustom)}
                  {s.isEdited && !s.isCustom && <span style={editedDotStyle} title="Edited" />}
                  {s.isCustom && <span style={customChipStyle}>custom</span>}
                </button>

                {!readOnly && (
                  <div style={sectionActionsStyle}>
                    <RowAction onClick={() => move(s.code, -1)} disabled={idx === 0} title="Move up">
                      <IconSystem.chevronUp size={12} />
                    </RowAction>
                    <RowAction onClick={() => move(s.code, 1)} disabled={idx === sections.length - 1} title="Move down">
                      <IconSystem.chevronDown size={12} />
                    </RowAction>
                    <RowAction onClick={() => handleRename(s.code, s.title)} title="Rename section">
                      <IconSystem.edit size={12} />
                    </RowAction>
                    {!s.isCustom && (
                      <RowAction
                        onClick={() => handleToggleHidden(s.code)}
                        title={s.isHidden ? 'Show in print' : 'Hide from print'}
                      >
                        {s.isHidden
                          ? <IconSystem.eye size={12} />
                          : <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>👁</span>}
                      </RowAction>
                    )}
                    {!s.isCustom && s.isEdited && (
                      <RowAction onClick={() => handleRestoreDefault(s.code)} title="Restore template default">
                        <IconSystem.refresh size={12} />
                      </RowAction>
                    )}
                    {s.isCustom && (
                      <RowAction onClick={() => handleRemove(s.code)} title="Remove custom section" danger>
                        <IconSystem.trash size={12} />
                      </RowAction>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <footer style={sectionListFooterStyle}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleAdd} style={{ width: '100%' }}>
              <IconSystem.add size={13} /> Add section
            </button>
          </footer>
        )}
      </aside>

      {renderEditor()}
      {rightPanel}
    </div>
  );

  function renderEditor() {
    return (
      <main style={editorPaneStyle}>
        {selected ? (
          <>
            <header style={editorHeaderStyle}>
              <h3 style={editorTitleStyle}>{selected.title}</h3>
              {selected.isCustom && <span style={customChipStyle}>custom</span>}
              {selected.isHidden && <span style={hiddenChipStyle}>hidden — won't print</span>}
              {renderTitleExtra?.(selected.code, selected.isCustom)}
              {headerRight && <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{headerRight}</span>}
            </header>
            {renderSectionHeader?.(selected.code, selected.isCustom)}
            <div style={editorContainerStyle}>
              <TiptapEditor
                sectionKey={`${contentVersion}:${restoreNonce}:${selected.code}:${selected.variantKey}`}
                initialContent={effectiveContent}
                onChange={handleEditorChange}
                readOnly={readOnly || selected.isHidden}
                dataInsertOptions={dataInsertOptions}
                fill
              />
            </div>
          </>
        ) : (
          <div style={emptyStateStyle}>Select a section to edit.</div>
        )}
      </main>
    );
  }
};

const RAIL_KEY = 'fsa.docEditor.railCollapsed';

// ============================================================================
// RowAction button
// ============================================================================

const RowAction: React.FC<{
  onClick: () => void;
  title: string;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ onClick, title, disabled, danger, children }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    disabled={disabled}
    title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 22,
      border: 'none', borderRadius: 3,
      background: 'transparent',
      color: disabled ? 'var(--text-muted, #94a3b8)' : danger ? '#dc2626' : 'var(--text-secondary)',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    {children}
  </button>
);

export function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'sec-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// ============================================================================
// Styles
// ============================================================================

const paneStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};
const sectionListStyle: React.CSSProperties = {
  width: 280,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const sectionListHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.4,
};
const railToggleStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24,
  border: 'none', borderRadius: 4, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
};
const collapsedRowStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '0.45rem 0', border: 'none', borderLeft: '3px solid transparent',
  fontSize: '0.8rem', color: 'var(--text-primary)', cursor: 'pointer', textAlign: 'center', fontVariantNumeric: 'tabular-nums',
};
const sectionListBodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '0.25rem 0',
};
const sectionListFooterStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderTop: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const sectionRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  borderLeft: '3px solid transparent',
};
const sectionRowMainStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem',
  padding: '0.4rem 0.5rem 0.4rem 0.75rem',
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
  fontSize: '0.8125rem',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  minWidth: 0,
};
const sectionActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  paddingRight: '0.25rem',
};
const sectionIndexStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  minWidth: 18,
  textAlign: 'right',
};
const sectionTitleStyle: React.CSSProperties = {
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const editedDotStyle: React.CSSProperties = {
  width: 6, height: 6,
  borderRadius: '50%',
  background: '#2563eb',
  flexShrink: 0,
};
const countStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const customChipStyle: React.CSSProperties = {
  padding: '1px 6px',
  borderRadius: 999,
  background: '#f3e8ff',
  color: '#6b21a8',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};
const hiddenChipStyle: React.CSSProperties = {
  padding: '1px 6px',
  borderRadius: 999,
  background: '#f1f5f9',
  color: '#64748b',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};
const editorPaneStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
};
const editorHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', minHeight: 40, boxSizing: 'border-box',
  padding: '0.5rem 0.875rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const editorTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const editorContainerStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '0.625rem 0.875rem',
  // The editor fills this and scrolls its own text, so its bar stays in view.
  overflow: 'hidden',
};
const emptyStateStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
};

export default SectionedNarrativeEditor;
